// PullRequestWork.tsx — the Work section of the PR page: where this change
// stands and which todos it carries. What waits on a person is the merge box's
// to say, once, not a Needs-you row repeated under it.
//
// A shift PR is the review end of the queue: one commit per todo, each with a
// `Todo:` trailer. The rail is the same twelve-stage work trace a todo's own
// page shows, from the same model, so the two pages never disagree about the
// same change; the carried todos link back to their pages.

import { Workflow } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import type { PullRequestSummary, ShiftTodo } from '../api/types';
import { LotEta } from '../components/flow/LotEta';
import { queueEntryFor } from '../components/merge/mergeQueueModel';
import { useRepoMergeQueue } from '../hooks/useMergeQueue';
import { EMPTY_CHANNELS, useRepoChannels } from '../hooks/useRepoChannels';
import { useFlow } from '../hooks/useFlow';
import { useShiftTodos } from '../hooks/useShift';
import { carriedTodoIds, type PullCommit } from './pullCommitsModel';
import { WorkTrace } from './shift/WorkTrace';
import { todoHref } from './shift/workPaths';
import { ladderReach, releaseLadder } from './releaseChannelsModel';
import { pullFacts, workTrace } from './shift/workTraceModel';

import './page.css';
import './shift/Shift.css';

export interface PullRequestWorkProps {
  summary: PullRequestSummary;
  commits: PullCommit[] | undefined;
  repoId: string | null;
  repoFullName: string;
  prNumber: string | null;
}

export function PullRequestWork({
  summary,
  commits,
  repoId,
  repoFullName,
  prNumber,
}: PullRequestWorkProps): JSX.Element {
  const ids = useMemo(() => carriedTodoIds(commits), [commits]);
  // The whole queue, the same request Work makes; a forge without one answers
  // an error and the ids below stand on their own.
  const todos = useShiftTodos(undefined);
  const flow = useFlow({});
  const carried = useMemo<ShiftTodo[]>(() => {
    const byId = new Map((todos.data?.todos ?? []).map((todo) => [todo.id, todo]));
    return ids.map((id) => byId.get(id)).filter((todo): todo is ShiftTodo => Boolean(todo));
  }, [ids, todos.data]);

  const queue = useRepoMergeQueue(repoId, summary.state === 'open');
  const entry = queueEntryFor(queue.data, prNumber);
  // The same release ladder the pull request lists read: a change with no
  // todo is released once the deepest channel carries it.
  const merged = summary.state === 'merged';
  const channels = useRepoChannels(merged ? [repoFullName] : []);
  const ladder = channels.byRepo.get(repoFullName) ?? EMPTY_CHANNELS;
  const released =
    merged && ladderReach(releaseLadder(summary, ladder.baselines, ladder.compares)) === 'done';
  const stages = workTrace(
    pullFacts({
      state: summary.state,
      draft: summary.draft,
      checks: summary.checks,
      review: summary.review,
      inQueue: Boolean(entry),
      queueFailed: entry?.state === 'failed' || entry?.state === 'dequeued',
      carries: carried,
      canMerge: summary.mergeable.can_merge,
      released,
    })
  );
  const subject = `${repoFullName}#${summary.number}`;

  return (
    <section className="pr-work" data-testid="pr-work" aria-label="Work">
      <h2 className="pr-commits__title">
        <Workflow aria-hidden="true" size={14} />
        Work
      </h2>
      <WorkTrace stages={stages} subject={subject} testId="pr-work-trace" />
      {summary.state === 'open' ? (
        <p className="pr-work__eta">
          <LotEta
            report={flow.data}
            lotRef={{ todoIds: ids, pull: { repo: repoFullName, number: summary.number } }}
            testId="pr-work-eta"
          />
        </p>
      ) : null}
      {ids.length === 0 ? (
        <p className="pr-work__empty" data-testid="pr-work-empty">
          No commit on this pull request names a todo.
        </p>
      ) : (
        <ul className="pr-work__carries" data-testid="pr-work-carries">
          {ids.map((id) => {
            const todo = carried.find((candidate) => candidate.id === id);
            return (
              <li className="pr-work__carry" key={id} data-testid={`pr-work-carry-${id}`}>
                carries todo <Link to={todoHref(id, todo?.family ?? '')}>{id}</Link>
                {todo ? ` — ${todo.title}` : ''}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
